import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
} from "@mui/material";

import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  Timestamp,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  deleteDoc,
  setDoc,
} from "firebase/firestore";

export default function ProductUnit() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
  });

  const [propertyId, setPropertyId] = useState("");
  const [units, setUnits] = useState([]);
  const [search, setSearch] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  const [unit, setUnit] = useState("");
  const [editId, setEditId] = useState("");
  const [editUnit, setEditUnit] = useState("");

  /* ================= FETCH PROPERTY ID ================= */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;

      const snap = await getDoc(doc(db, "authroles", user.uid));
      if (!snap.exists()) {
        Toast.fire({ icon: "error", title: "Property not assigned" });
        return;
      }

      setPropertyId(snap.data().propertyId);
    });

    return () => unsub();
  }, []);

  /* ================= FETCH PRODUCT UNITS ================= */
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "productunits"),
      where("propertyId", "==", propertyId)
    );

    return onSnapshot(q, (snap) => {
      setUnits(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    });
  }, [propertyId]);

  /* ================= ADD ================= */
  const handleAdd = async () => {
    if (!unit.trim()) {
      Toast.fire({ icon: "error", title: "Enter product unit" });
      return;
    }

    await addDoc(collection(db, "productunits"), {
      propertyId,
      unit: unit.trim(),
      createdAt: Timestamp.now(),
    });

    setUnit("");
    setOpenAdd(false);
    Toast.fire({ icon: "success", title: "Product unit added" });
  };

  /* ================= EDIT ================= */
  const openEditModal = (row) => {
    setEditId(row.id);
    setEditUnit(row.unit);
    setOpenEdit(true);
  };

  const handleUpdate = async () => {
    if (!editUnit.trim()) return;

    await setDoc(
      doc(db, "productunits", editId),
      {
        unit: editUnit.trim(),
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );

    setOpenEdit(false);
    Toast.fire({ icon: "success", title: "Product unit updated" });
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "productunits", id));
    Toast.fire({ icon: "success", title: "Product unit deleted" });
  };

  /* ================= FILTER ================= */
  const filteredUnits = units.filter((u) =>
    u.unit.toLowerCase().includes(search.toLowerCase())
  );

  /* ================= UI ================= */
  return (
    <Box className=" p-3">
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Product Units</Typography>
            <Button variant="contained" onClick={() => setOpenAdd(true)}>
              Add Unit
            </Button>
          </Stack>

          <TextField
            fullWidth
            size="small"
            placeholder="Search unit..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUnits.map((row, i) => (
                  <TableRow key={row.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{row.unit}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openEditModal(row)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDelete(row.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ADD DIALOG */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth>
        <DialogTitle>Add Product Unit</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Unit"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth>
        <DialogTitle>Edit Product Unit</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Unit"
            value={editUnit}
            onChange={(e) => setEditUnit(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
